import React, { useCallback, useDeferredValue, useMemo, useState } from "react";
import { AHIcon, AHSeparator, Box } from "arkh";
import { graphql, useFragment } from "react-relay";

import { FlatList, View } from "react-native";

import { WorkspaceTodoDetailList_todoWorkspace$key } from "./__generated__/WorkspaceTodoDetailList_todoWorkspace.graphql";
import { useOrgListStyleProps } from "../../useOrgHomeDetailContainerStyle";

import { WorkspaceTodoStoresListItem } from "./WorkspaceTodoStoresListItem";

import {
  WorkspaceTodoListHeader,
  WorkspaceTodoListHeaderGallery,
  WorkspaceTodoOrder,
} from "./WorkspaceTodoListHeader";
import { WorkspaceEmptyListItem } from "../../WorkspaceEmptyListItem";
import { SegmentedControlNew } from "@/src/components/SegmentedControlNew/SegmentedControlNew";
import { WorkspaceTodoStoresGalleryItem } from "./WorkspaceTodoStoresGalleryItem";

const DEFAULT_ORDER: WorkspaceTodoOrder = {
  column: "store",
  order: "DESC",
};
const NUMBER_OF_ITEMS_PER_ROW = 3;
export function WorkspaceTodoStoresList(props: {
  ListHeaderComponent: React.ReactElement;
  todoWorkspace: WorkspaceTodoDetailList_todoWorkspace$key;
  onPress(todoId: string, storeName: string, timezone: string): void;
  openProduct(id: string): void;
}) {
  const [search, setSearch] = React.useState("");
  const query = useDeferredValue(search);
  const [_order, setOrder] = React.useState<WorkspaceTodoOrder>(DEFAULT_ORDER);

  const data = useFragment(
    graphql`
      fragment WorkspaceTodoDetailList_todoWorkspace on TodoWorkspace {
        ...WorkspaceTodoListHeader_todoWorkspace

        archivedAtDate {
          __typename
        }
        responseType
        todos(workspaceId: $workspaceId) {
          id
          store {
            name
          }
          commentCount
          resolvedAtDate {
            isoString
          }
          response {
            __typename
            ... on TodoResponseImage {
              images {
                id
              }
            }
          }
          ...WorkspaceTodoStoresGalleryItem_todo
          ...WorkspaceTodoStoresListItem_todo
        }
      }
    `,
    props.todoWorkspace
  );

  const hasGallery = data.responseType === "IMAGES" ? true : false;

  const [viewType, setViewType] = useState<"list" | "gallery">("list");
  const order = useMemo(() => {
    return viewType === "gallery" ? DEFAULT_ORDER : _order;
  }, [viewType, _order]);

  if (viewType === "gallery" && !hasGallery) {
    setViewType("list");
  }

  const filteredTodos = useMemo(() => {
    return query.length > 0
      ? data.todos.filter((item) =>
          item.store?.name
            .toLocaleLowerCase()
            .includes(query.toLocaleLowerCase())
        )
      : data.todos.slice();
  }, [data.todos, query]);

  const listData = useMemo(() => {
    const sort = (
      a: (typeof filteredTodos)[number],
      b: (typeof filteredTodos)[number]
    ) => {
      switch (order.column) {
        case "store":
          return a.store?.name.localeCompare(b.store?.name ?? "") ?? 0;
        case "comments":
          return (b.commentCount ?? 0) - (a.commentCount ?? 0);
        case "completedAt":
        case "status":
          if (a.resolvedAtDate?.isoString === b.resolvedAtDate?.isoString) {
            return b.store?.name?.localeCompare(a.store?.name ?? "") ?? 0;
          }
          if (a.resolvedAtDate === null) {
            return -1;
          }
          if (b.resolvedAtDate === null) {
            return 1;
          }
          return (
            b.resolvedAtDate?.isoString?.localeCompare(
              a.resolvedAtDate?.isoString ?? ""
            ) ?? 0
          );
      }
    };
    const sortedOcccasions = filteredTodos.sort((a, b) =>
      order.order === "ASC" ? -sort(a, b) : sort(a, b)
    );
    if (viewType === "gallery") {
      return [
        "gallery-header" as const,
        ...batchArray(
          sortedOcccasions.filter(
            (todo) =>
              todo.response?.__typename === "TodoResponseImage" &&
              todo.response.images.length > 0
          ),
          NUMBER_OF_ITEMS_PER_ROW
        ),
      ] as const;
    }
    return ["header" as const, ...sortedOcccasions] as const;
  }, [filteredTodos, order.column, order.order, viewType]);

  const listProps = useOrgListStyleProps();

  type ListItem = (typeof listData)[number];
  type Row = ListItem | typeof filteredTodos;

  const renderItem = useCallback(
    ({ item, index }: { item: Row; index: number }) => {
      if (item === "gallery-header") {
        return (
          <WorkspaceTodoListHeaderGallery
            search={search}
            setSearch={setSearch}
          />
        );
      }
      if (item === "header") {
        return (
          <WorkspaceTodoListHeader
            search={search}
            setSearch={setSearch}
            order={order}
            workspaceTodo={data}
            setOrderBy={setOrder}
          />
        );
      }

      if (Array.isArray(item)) {
        return (
          <View style={{ flexDirection: "row" }}>
            {item.map((todo) => (
              <View
                key={todo.id}
                style={{ width: `${100 / NUMBER_OF_ITEMS_PER_ROW}%` }}
              >
                <WorkspaceTodoStoresGalleryItem
                  todo={todo}
                  isLast={listData.length - 1 === index}
                  onPress={props.onPress}
                  disabled={data.archivedAtDate !== null}
                />
              </View>
            ))}
          </View>
        );
      }

      return (
        <WorkspaceTodoStoresListItem
          todo={item}
          isLast={listData.length - 1 === index}
          onPress={props.onPress}
          disabled={data.archivedAtDate !== null}
        />
      );
    },
    [data, listData.length, order, props.onPress, search]
  );

  return (
    <FlatList
      {...listProps}
      ListHeaderComponent={
        <>
          {props.ListHeaderComponent}
          {hasGallery ? (
            <Box spaceTop="medium" style={{ flexDirection: "row" }}>
              <SegmentedControlNew
                items={[
                  {
                    children: <AHIcon type="reorder" />,
                    width: 100,
                    value: "list",
                  },
                  {
                    width: 100,
                    children: <AHIcon type="image" />,
                    value: "gallery",
                  },
                ]}
                selected={viewType}
                setSelected={(selected) => {
                  setViewType(selected);
                }}
              />
            </Box>
          ) : null}
        </>
      }
      ItemSeparatorComponent={viewType === "gallery" ? undefined : AHSeparator}
      data={listData}
      ListFooterComponent={
        filteredTodos.length === 0 ? (
          <View>
            <AHSeparator />
            <WorkspaceEmptyListItem />
          </View>
        ) : null
      }
      stickyHeaderIndices={[1]}
      renderItem={renderItem}
    />
  );
}

function batchArray<T>(array: T[], size: number): T[][] {
  return array.reduce((acc, todo, index) => {
    const batchIndex = Math.floor(index / size);
    acc[batchIndex] = [...(acc[batchIndex] || []), todo];
    return acc;
  }, [] as T[][]);
}
